/**
 * Button component using brand-tokens via Tailwind CSS
 * Migrated from @wineryxplorer/design-system
 */

import React from 'react';

export interface ButtonProps {
  // Variant semantics:
  // - `primary`   → default action color (olive). Most CTAs — Submit, Create,
  //    Edit, Manage, Save, card-level actions. This is the DEFAULT variant.
  // - `secondary` → rare burgundy accent. Reserve for the single most
  //    important page-level punctuation (top-tier rating, hero accent).
  //    NOT a destructive color — use `danger` for destructive.
  // - `outline`   → quieter alternative (olive-bordered).
  // - `ghost`     → text-only action, olive-tinted.
  // - `link`      → inline link-style action.
  // - `danger`    → destructive / high-consequence (warm crimson error).
  // - `dangerGhost` → destructive, but text-only. For a destructive action that
  //    sits inline in a dense list row, where a filled `danger` button would
  //    shout. Carries the crimson at rest, not just on hover — `ghost` plus a
  //    `text-error` class does NOT work, because `.text-primary-800` is emitted
  //    after `.text-error` and wins the tie.
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'link'
    | 'danger'
    | 'dangerGhost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-pressed'?: boolean;
  'aria-expanded'?: boolean;
  'aria-selected'?: boolean;
  'aria-controls'?: string;
  id?: string;
  role?: string;
}

// Anchored to the swapped scales: primary = olive (#4A5236), secondary =
// burgundy (#6B1D2B). Outline/ghost/link route through `primary-*` so the
// quiet variants resolve to olive. Burgundy via `secondary` is reserved as
// a rare editorial accent.
const variantClasses = {
  primary:   'bg-primary-700 text-white hover:bg-primary-800 focus:ring-info-500 disabled:bg-primary-300',
  secondary: 'bg-secondary-700 text-white hover:bg-secondary-800 focus:ring-info-500 disabled:bg-secondary-300',
  // `outline` and `ghost` wash on hover with a 10% tint of the action color,
  // not `primary-50`. `primary-50` resolves to rgb(246 247 241), which against
  // the #FAF9F6 ground is a ~2-value step — the hover state was invisible
  // wherever either variant was used. `dangerGhost` mirrors this with crimson.
  outline:
    'border-2 border-primary-700 text-primary-800 hover:bg-primary-700/10 focus:ring-info-500 disabled:border-primary-300 disabled:text-primary-300',
  ghost:   'text-primary-800 hover:bg-primary-700/10 focus:ring-info-500 disabled:text-primary-300',
  link:    'text-primary-800 hover:text-primary-900 underline-offset-4 hover:underline focus:ring-info-500 disabled:text-primary-300',
  danger:  'bg-error text-white hover:brightness-95 focus:ring-info-500 disabled:opacity-50',
  dangerGhost:
    'text-error hover:bg-error/10 focus:ring-info-500 disabled:opacity-50',
};

const sizeClasses = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
  xl: 'px-8 py-4 text-xl',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  children,
  onClick,
  type = 'button',
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-pressed': ariaPressed,
  'aria-expanded': ariaExpanded,
  'aria-selected': ariaSelected,
  'aria-controls': ariaControls,
  id,
  role,
}) => {
  const baseClasses =
    'inline-flex items-center justify-center font-sans font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 touch-manipulation';

  // Ensure minimum touch target size for mobile accessibility
  const touchTargetClasses = size === 'sm' ? 'min-h-[44px]' : '';

  return (
    <button
      id={id}
      type={type}
      role={role}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      aria-selected={ariaSelected}
      aria-controls={ariaControls}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${touchTargetClasses} ${fullWidth ? 'w-full' : ''} ${disabled || loading ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
    >
      {loading && (
        <span className="mr-2 animate-spin" aria-hidden="true">
          ⏳
        </span>
      )}
      {children}
    </button>
  );
};

// Convenience components for common button patterns
export const PrimaryButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="primary" {...props} />
);

export const SecondaryButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="secondary" {...props} />
);

export const OutlineButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="outline" {...props} />
);

export const GhostButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="ghost" {...props} />
);

export const LinkButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="link" {...props} />
);

// Form-specific button components
export const SubmitButton: React.FC<Omit<ButtonProps, 'type'>> = (props) => (
  <Button type="submit" {...props} />
);

export const CancelButton: React.FC<Omit<ButtonProps, 'variant' | 'type'>> = (props) => (
  <Button variant="outline" type="button" {...props} />
);

export const DeleteButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="danger" {...props} />
);